import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CashIcon,
  costFormater,
  Hint,
  InfoOutlineIcon,
  Inscription,
  LinkIcon,
  ListItem,
  ListItemBase,
  ListItemType,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import { toString } from 'lodash';

import { RiskControlOptions } from '@/common/constants';
import { IconWrapper } from '@/modules/investments/hooks/useInvestmentsHoldings';
import { Investment } from '@/modules/investments/investments.types';
import colors from '@/styles/colors';
import theme from '@/styles/theme';

import { useInvestmentPortfolios } from '../queries/useInvestmentPortfolios.query';

import { useGetFundIcon } from '.';

const CASH_KEY = 'Cash';
const headers: ListItemBase[] = [
  {
    key: 'fundName',
    title: 'Fund name',
    flex: 4,
  },
  {
    key: 'type',
    title: 'Type',
  },
  {
    key: 'election',
    title: 'Election',
  },
  {
    key: 'amount',
    title: 'Amount',
  },
  {
    key: 'research',
    title: 'Research',
    flex: 2,
    endAlign: true,
  },
];

interface Params {
  riskValue: RiskControlOptions;
  investorId?: string | null;
  investmentAmount?: number;
  onOpen?: (item: Investment) => void;
}
export default ({ riskValue, investorId, investmentAmount, onOpen }: Params) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { data, isLoading, isSuccess, refetch } = useInvestmentPortfolios({
    investor_id: investorId,
    risk_model: riskValue,
    amount: investmentAmount,
    isOnlyActive: true,
  });

  const iconLabel = useMemo(
    () => (
      <IconWrapper margin={{ left: 'spacing8' }}>
        <InfoOutlineIcon color="iconSecondary" size="20px" />
      </IconWrapper>
    ),
    []
  );
  const handleGetFundIcon = useGetFundIcon();
  const formatedList = useMemo<ListItem[]>(
    () => {
      if (isMobile) {
        return data?.map((item) => {
          const formattedItem: ListItem = {
            id: toString(item.instrumentId),
            onPick: () => {
              if (onOpen) {
                onOpen(item as Investment);
              }
            },
            rowStyle: {
              padding: theme.spacings.spacing16,
            },
            fields: [
              {
                key: 'fundName',
                flex: 4,
                type: ListItemType.Node,
                title: '',
                node: (
                  <Box direction="row" align="center">
                    {item.fundName === CASH_KEY && !item.type ? (
                      <CashIcon />
                    ) : (
                      handleGetFundIcon({
                        testId: `investment-fund_item-${item.instrumentId}`,
                        image: item.imageUrl,
                        status: item.status,
                      })
                    )}
                    <Box direction="column" pad={{ left: 'spacing12' }}>
                      <Box direction="row" align="center">
                        <Text
                          data-testid={`investment-fund_item-${item.instrumentId}_fund-name`}
                          weight="bold"
                          truncate
                          title={item.fundName}
                        >
                          {item.fundName}
                        </Text>
                        {item.fundName === CASH_KEY && !item.type && (
                          <Hint hintElement={iconLabel}>
                            <Text textAlign="center">
                              {t(
                                'DriveWealth keeps some amount of cash for market trades. The amount depends on your risk category level.'
                              )}
                            </Text>
                          </Hint>
                        )}
                      </Box>
                      <Text>{item.symbol || item.type}</Text>
                    </Box>
                  </Box>
                ),
              },

              {
                key: 'amount',
                type: ListItemType.Node,
                title: '',
                flex: 1,
                node: (
                  <Box direction="column" align="end">
                    <Text weight="bold" data-testid={`investment-fund_item-${item.instrumentId}_amount`}>
                      {costFormater(item.amount, true)}
                    </Text>

                    <Text>{`${(item.election || 0).toFixed(2)}%`}</Text>
                  </Box>
                ),
              },
            ],
          };
          return formattedItem;
        });
      }
      return data?.map((item) => {
        const newItem: ListItem = {
          id: toString(item.instrumentId),

          fields: [
            {
              key: 'fundName',
              type: ListItemType.Node,
              title: '',
              node: (
                <Box direction="row" align="center">
                  {item.fundName === CASH_KEY && !item.type ? (
                    <CashIcon />
                  ) : (
                    handleGetFundIcon({
                      testId: `investment-fund_item-${item.instrumentId}`,
                      image: item.imageUrl,
                      status: item.status,
                    })
                  )}

                  <Text
                    data-testid={`investment-fund_item-${item.instrumentId}_fund-name`}
                    weight="bold"
                    margin={{ left: 's' }}
                  >
                    {item.fundName}
                  </Text>
                  {item.fundName === CASH_KEY && !item.type && (
                    <Hint hintElement={iconLabel}>
                      <Text textAlign="center">
                        {t(
                          'DriveWealth keeps some amount of cash for market trades. The amount depends on your risk category level.'
                        )}
                      </Text>
                    </Hint>
                  )}
                </Box>
              ),
              flex: 4,
            },
            {
              key: 'type',
              type: ListItemType.Text,
              title: item.type || '',
            },
            {
              key: 'election',
              type: ListItemType.Text,
              title: `${(item.election || 0).toFixed(2)}%`,
            },
            {
              key: 'amount',
              type: ListItemType.Node,
              title: '',
              node: (
                <Text weight="bold" data-testid={`investment-fund_item-${item.instrumentId}_amount`}>
                  {costFormater(item.amount, true)}
                </Text>
              ),
            },
            {
              key: 'research',
              type: ListItemType.Node,
              title: '',
              hover: true,
              node:
                item.fundName === CASH_KEY && !item.type ? (
                  <Text textAlign="center">-</Text>
                ) : (
                  <a
                    href={item?.researchUrl}
                    data-testid={`investment-fund_item-${item.instrumentId}_link`}
                    target="_blank"
                    style={{ textDecoration: 'none' }}
                    rel="noreferrer"
                  >
                    <Box direction="row" align="center" justify="end" style={{ cursor: 'pointer' }}>
                      <Inscription color="textAccent" textAlign="end" margin={{ right: 'spacing8' }}>
                        Fund research
                      </Inscription>
                      <Box direction="row" align="center" justify="center">
                        <LinkIcon size="18px" color={colors.iconAccent} />
                      </Box>
                    </Box>
                  </a>
                ),
              flex: 2,
            },
          ],
        };
        return newItem;
      });
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, iconLabel, t]
  );

  return {
    formatedList: formatedList || [],
    data,
    headers,
    isLoading,
    isSuccess,
    refetch,
  };
};
